import React from 'react'
import clsx from 'clsx'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'
import { styled } from '@mui/material/styles'

import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import WebCam from 'react-webcam'

import CameraAltIcon from '@mui/icons-material/CameraAlt'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import ThreeSixtyIcon from '@mui/icons-material/ThreeSixty'

import { ActionsRow, buttonSx, CardIconButton, ModalFooter } from 'src/utils/views'

import { SearchByPictureScope } from '../tc_scopes'

const LOG = Logger.get('SearchByPictureView')

const ModalView = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    color: 'var(--ink)',
    fontSize: 14,
    lineHeight: 1.45
})

const ModalHead = styled('div')({
    padding: '24px 26px 0',
    '& h2': { margin: '0 0 5px', fontSize: 20, lineHeight: 1.2 },
    '& p': { margin: 0, color: 'var(--muted)' }
})

const ModalContent = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    padding: '18px 26px 20px',
    '& video': {
        maxWidth: '100%',
        borderRadius: 11,
        border: '1px solid var(--border)',
        backgroundColor: 'var(--surface-2)'
    }
})

const NoCameraNote = styled('div')({
    color: 'var(--muted)'
})

const { userVideoConstraints, environmentVideoConstraints } = (() => {
    const width = 640
    const height = 480

    const userVideoConstraints: MediaTrackConstraints = {
        width,
        height,
        facingMode: 'user'
    }

    const environmentVideoConstraints: MediaTrackConstraints = {
        width,
        height,
        facingMode: 'environment'
    }

    return { userVideoConstraints, environmentVideoConstraints }
})()

// ::

export type SearchByPictureViewProps = {
    className?: string
    scope: SearchByPictureScope
}

export const SearchByPictureView = function ({ className, scope }: SearchByPictureViewProps) {
    bindUpdate(React, scope)

    const webcamRef = React.useRef<WebCam>(null)

    const haddleOnCaptureClick = React.useCallback(() => {
        if (webcamRef.current) {
            const imageSrc = webcamRef.current.getScreenshot()
            if (imageSrc) {
                scope.onCapture(imageSrc).catch(LOG.caught)
            }
        }
    }, [webcamRef, scope])

    const handleOnFileUpload = React.useCallback(() => {
        LOG.debug('handleOnFileUpload')
    }, [scope])

    const handleOnFlipCamera = React.useCallback(() => {
        scope.userFace = !scope.userFace
    }, [scope])

    const handleOnClose = React.useCallback(() => {
        scope.onClose().catch(LOG.caught)
    }, [scope])

    const hasVideoCamera = !!(scope.frontCamera || scope.rearCamera)
    const hasFrontAndReaderCamera = !!(scope.frontCamera && scope.rearCamera)

    let videoConstrainst = userVideoConstraints
    if (hasFrontAndReaderCamera) {
        videoConstrainst = scope.userFace ? userVideoConstraints : environmentVideoConstraints
    }

    return (
        <ModalView className={clsx(className)}>
            <ModalHead>
                <h2>Busca por foto</h2>
                <p>Enquadre o rosto na câmera e tire a foto para procurar a pessoa na base.</p>
            </ModalHead>
            <ModalContent>
                {hasVideoCamera && (
                    <WebCam
                        ref={webcamRef}
                        audio={false}
                        screenshotFormat="image/jpeg"
                        videoConstraints={videoConstrainst}
                    />
                )}
                {!hasVideoCamera && <NoCameraNote>Nenhuma câmera disponível neste dispositivo.</NoCameraNote>}
            </ModalContent>
            <ModalFooter>
                <ActionsRow>
                    <Tooltip title="Enviar um arquivo de imagem">
                        <CardIconButton aria-label="Enviar um arquivo de imagem" onClick={handleOnFileUpload}>
                            <FileUploadIcon />
                        </CardIconButton>
                    </Tooltip>
                    {hasFrontAndReaderCamera && (
                        <Tooltip title="Alternar entre a câmera frontal e a traseira">
                            <CardIconButton
                                aria-label="Alternar entre a câmera frontal e a traseira"
                                onClick={handleOnFlipCamera}
                            >
                                <ThreeSixtyIcon />
                            </CardIconButton>
                        </Tooltip>
                    )}
                </ActionsRow>
                <ActionsRow>
                    <Button sx={buttonSx()} onClick={handleOnClose}>
                        Fechar
                    </Button>
                    <Button
                        sx={buttonSx('primary')}
                        startIcon={<CameraAltIcon />}
                        disabled={!hasVideoCamera}
                        onClick={haddleOnCaptureClick}
                    >
                        Tirar foto
                    </Button>
                </ActionsRow>
            </ModalFooter>
        </ModalView>
    )
}
